import React from 'react';
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme,
  useMediaQuery,
  Stack,
  Button,
  Tooltip,
  Divider,
  Skeleton,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { OpenInNew as OpenInNewIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import RequestBadges from '../RequestBadges';

interface Enrollment {
  firstName: string;
  lastName: string;
  email: string;
  city: string;
  phone: string;
}

interface EnrollmentListTableProps {
  data: Enrollment[];
  requestInfo: any;
  setCurrentEnrrol: (enrollment: Enrollment) => void;
  /** Muestra skeletons mientras carga la pagina. */
  loading?: boolean;
  /** Recarga el listado con la pagina y los filtros actuales. */
  onRefresh?: () => void;
}

/**
 * Datos de contacto de una fila.
 *
 * En el flujo diferido `info_dwolla` esta vacio (nadie lleno el formulario), asi
 * que se cae al correo del usuario que ahora devuelve el backend. Sin esto, esas
 * filas aparecian completamente en blanco y el admin no sabia de quien eran.
 */
const contactOf = (flat: any, raw: any) => ({
  firstName: flat?.firstName || '',
  lastName: flat?.lastName || '',
  email: flat?.email || raw?.user_email || '',
  city: flat?.city || '',
  phone: flat?.phone || '',
});

const formatDate = (date?: string) => (date ? moment(date).format('MMM D, YYYY h:mm A') : 'N/A');

const EnrollmentListTable: React.FC<EnrollmentListTableProps> = ({
  data,
  requestInfo,
  setCurrentEnrrol,
  loading = false,
  onRefresh,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const rows = Array.isArray(data) ? data : [];
  const rawRows = Array.isArray(requestInfo) ? requestInfo : [];

  const getInitials = (firstName: string = '', lastName: string = '') => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const handleSetEnrollment = (enrollment: Enrollment) => {
    setCurrentEnrrol(enrollment); // Llamamos al setState para actualizar el valor
  };

  /**
   * Propósito: Recarga la tabla de solicitudes sin cambiar de página ni filtros.
   */
  const handleRefreshList = () => {
    if (!onRefresh || loading) {
      /**
       * Output:
       * Side effects — No pide de nuevo si ya está cargando o no hay handler
       */
      return;
    }
    /**
     * Output:
     * Side effects — Dispara GET /requests en el backoffice
     */
    onRefresh();
  };

  /**
   * Luminancia relativa de un color, segun la formula de WCAG.
   *
   * Acepta hex (`#f7971e`, que es lo que devuelve la paleta de MUI) y
   * `rgb()`/`rgba()` (que es lo que devuelve el navegador). Solo mirar un
   * formato hacia que los colores hex cayeran en `NaN` y la eleccion de texto
   * se volviera siempre la misma.
   */
  const relativeLuminance = (color: string): number => {
    const hex = color.trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);

    const channels = hex
      ? (hex[1].length === 3 ? hex[1].split('') : [
          hex[1].slice(0, 2),
          hex[1].slice(2, 4),
          hex[1].slice(4, 6),
        ]).map((pair) =>
          Number.parseInt(pair.length === 1 ? pair + pair : pair, 16)
        )
      : color
          .replace(/rgba?\(|\)/g, '')
          .split(',')
          .slice(0, 3)
          .map((value) => Number.parseFloat(value));

    const linear = channels.map((channel) => {
      const normalized = channel / 255;
      return normalized <= 0.04045
        ? normalized / 12.92
        : ((normalized + 0.055) / 1.055) ** 2.4;
    });

    return 0.2126 * linear[0] + 0.7152 * linear[1] + 0.0722 * linear[2];
  };

  /** Ratio de contraste entre dos luminancias, segun WCAG. */
  const contrastRatio = (first: number, second: number): number => {
    const lighter = Math.max(first, second);
    const darker = Math.min(first, second);
    return (lighter + 0.05) / (darker + 0.05);
  };

  /**
   * Devuelve fondo y color de texto del avatar.
   *
   * Los diez colores de la lista salen de la paleta del tema, no de hex sueltos:
   * los anteriores no pertenecian a ninguna paleta de la app y no cambiaban con
   * el tema. La eleccion sigue siendo determinista por nombre.
   *
   * No se usa un umbral de luminancia para decidir el texto: se calcula el
   * contraste real de las dos opciones (claro y oscuro) contra el fondo y se
   * elige la que gana. Un umbral fijo falla justo en los tonos medios, que es
   * donde estan el ambar de aviso y el rojo de error.
   */
  const getAvatarStyle = (name: string) => {
    const colors = [
      theme.palette.primary.main,
      theme.palette.primary.light,
      theme.palette.primary.dark,
      theme.palette.secondary.main,
      theme.palette.secondary.light,
      theme.palette.info.main,
      theme.palette.success.main,
      theme.palette.warning.main,
      theme.palette.error.main,
      theme.palette.text.secondary,
    ];
    const index = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const backgroundColor = colors[index % colors.length];

    const lightText = theme.palette.primary.contrastText;
    const darkText = theme.palette.background.default;
    const background = relativeLuminance(backgroundColor);
    const lightWins =
      contrastRatio(relativeLuminance(lightText), background) >=
      contrastRatio(relativeLuminance(darkText), background);

    return {
      backgroundColor,
      color: lightWins ? lightText : darkText,
    };
  };

  const renderLoading = () => (
    <Box sx={{ p: 1 }}>
      {[0, 1, 2, 3].map((row) => (
        <Skeleton
          key={row}
          variant='rectangular'
          height={56}
          sx={{ mb: 1 }}
        />
      ))}
    </Box>
  );

  const renderEmpty = () => (
    <Typography
      color='text.secondary'
      align='center'
      sx={{ py: 4 }}
    >
      No requests match these filters
    </Typography>
  );

  const MobileCard: React.FC<{ enrollment: Enrollment; index: number }> = ({
    enrollment,
    index,
  }) => {
    const contact = contactOf(enrollment, rawRows[index]);

    return (
      <Card
        sx={{
          mb: 2,
          backgroundColor: 'background.paper',
          '&:hover': {
            boxShadow: 6,
            transform: 'translateY(-2px)',
            transition: 'all 0.2s',
          },
        }}
      >
        <CardContent>
          <Stack direction='row' spacing={2} alignItems='center' mb={2}>
            <Avatar
              sx={{
                ...getAvatarStyle(`${contact.firstName} ${contact.lastName}`),
                width: 56,
                height: 56,
              }}
            >
              {getInitials(contact.firstName, contact.lastName)}
            </Avatar>
            <Box>
              <Typography variant='h6' component='div'>
                {contact.firstName} {contact.lastName}
              </Typography>
              <Typography color='text.secondary' variant='body2'>
                {contact.email}
              </Typography>
            </Box>
          </Stack>
          <Stack spacing={1}>
            <Typography variant='body2'>
              <strong>City:</strong> {contact.city}
            </Typography>
            <Typography variant='body2'>
              <strong>Phone:</strong> {contact.phone || 'N/A'}
            </Typography>
            <Typography variant='body2'>
              <strong>Request Date:</strong> {formatDate(rawRows[index]?.date_created)}
            </Typography>
            <Box>
              <RequestBadges request={rawRows[index]} />
            </Box>
            <Button
              variant='contained'
              startIcon={<OpenInNewIcon />}
              fullWidth
              onClick={() => handleSetEnrollment(enrollment)}
              sx={{ mt: 2 }}
            >
              View Details
            </Button>
          </Stack>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ px: 3, pt: 3, pb: 1, bgcolor: 'background.default' }}>
      <Container maxWidth='xl'>
        <Stack spacing={4}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 2,
            }}
          >
            <Box>
              <Typography variant='h4' fontWeight='bold' gutterBottom>
                Enrollment Requests
              </Typography>
              <Divider sx={{ mb: 3, borderColor: 'divider' }} />
              <Typography color='text.secondary' variant='subtitle1'>
                Manage and review enrollment requests
              </Typography>
            </Box>
            <Tooltip title='Refresh list'>
              <span>
                <IconButton
                  aria-label='Refresh enrollment requests'
                  onClick={handleRefreshList}
                  disabled={loading || !onRefresh}
                >
                  {loading ? (
                    <CircularProgress size={22} color='inherit' />
                  ) : (
                    <RefreshIcon />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          {isMobile ? (
            <Box>
              {loading ? (
                renderLoading()
              ) : rows.length === 0 ? (
                renderEmpty()
              ) : (
                rows.map((enrollment, index) => (
                  <MobileCard
                    key={rawRows[index]?.id ?? index}
                    enrollment={enrollment}
                    index={index}
                  />
                ))
              )}
            </Box>
          ) : (
            <TableContainer
              component={Paper}
              sx={{
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                minWidth: 600,
              }}
            >
              <Table>
                <TableHead>
                  {/*
                    La cabecera era negro puro con texto blanco: dos literales
                    que no existian en el tema. `bg-surface-overlay` es la
                    superficie elevada de los tokens, asi que la cabecera se
                    distingue del cuerpo y funciona en claro y oscuro.
                  */}
                  <TableRow className='bg-surface-overlay'>
                    <TableCell sx={{ color: 'text.primary', fontWeight: 'bold' }}>Name</TableCell>
                    <TableCell sx={{ color: 'text.primary', fontWeight: 'bold' }}>Email</TableCell>
                    <TableCell sx={{ color: 'text.primary', fontWeight: 'bold' }}>City</TableCell>
                    <TableCell sx={{ color: 'text.primary', fontWeight: 'bold' }}>Phone</TableCell>
                    <TableCell sx={{ color: 'text.primary', fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell sx={{ color: 'text.primary', fontWeight: 'bold' }}>Request Date</TableCell>
                    <TableCell align='right' sx={{ color: 'text.primary', fontWeight: 'bold' }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7}>{renderLoading()}</TableCell>
                    </TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align='center'>
                        {renderEmpty()}
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((enrollment, index) => {
                      const contact = contactOf(enrollment, rawRows[index]);

                      return (
                        <TableRow
                          // El correo de `info_dwolla` puede venir vacio en las
                          // diferidas; se usa el id de la solicitud, que siempre
                          // existe y es unico.
                          key={rawRows[index]?.id ?? index}
                          sx={{
                            // Rayado y hover salen del tema: los hex claros
                            // originales dejaban filas blancas en modo oscuro.
                            '&:nth-of-type(odd)': { backgroundColor: 'background.default' },
                            '&:hover': { backgroundColor: 'action.hover' },
                          }}
                        >
                          <TableCell>
                            <Stack direction='row' spacing={2} alignItems='center'>
                              <Avatar
                                sx={{
                                  ...getAvatarStyle(`${contact.firstName} ${contact.lastName}`),
                                }}
                              >
                                {getInitials(contact.firstName, contact.lastName)}
                              </Avatar>
                              <Typography>
                                {contact.firstName} {contact.lastName}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell>{contact.email}</TableCell>
                          <TableCell>{contact.city}</TableCell>
                          <TableCell>{contact.phone || 'N/A'}</TableCell>
                          <TableCell>
                            <RequestBadges request={rawRows[index]} />
                          </TableCell>
                          <TableCell>{formatDate(rawRows[index]?.date_created)}</TableCell>
                          <TableCell align='right'>
                            <Tooltip title='View Details' arrow>
                              <Button
                                variant='contained'
                                size='small'
                                // El relleno negro con su hover se elimina:
                                // el boton es `contained`, asi que el tema ya le
                                // da el color de marca y su propio hover.
                                sx={{ mr: 1 }}
                                onClick={() => handleSetEnrollment(enrollment)}
                              >
                                <OpenInNewIcon fontSize='small' />
                              </Button>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Stack>
      </Container>
    </Box>
  );
};

export default EnrollmentListTable;
